// @flow
import React from 'react'
import ReactDOM from 'react-dom'
import ResizeObserver from 'resize-observer-polyfill'
import OlMap from './ol_map'
import type { AnimateDataType, AnimateConfigType, MapfileDataType } from './layer_animation'

export type ViewType = {
    center: [number, number], // ol.Coordinate
    resolution: number,
    rotation: number,
    mapScale: number,
}

export type ExtentType = [number, number, number, number]

type ClickType = {
    coordinate: [number, number],
}

// Re-exports
// Tweak the name so I can re-export this type
//   https://github.com/facebook/flow/issues/1379
export type LayerAnimationType = AnimateDataType
export type LayerAnimationConfigType = AnimateConfigType
export type MapfileAnimationType = MapfileDataType

type PropType = {
    baseLayer?: string,
    // Map coordinates of the click (3857)
    click?: ClickType,
    extent?: ExtentType,
    layerAnimationData?: AnimateDataType,
    map: {
        mapExtentUpdateHandled: Function,
        mapViewUpdateHandled: Function,
        needsMapExtentUpdate: boolean,
        needsMapViewUpdate: boolean,
        view: ViewType,
    },
    mapScale?: number,
    mapExtentUpdateHandled: Function,
    mapViewUpdateHandled: Function,
    needsMapExtentUpdate: boolean,
    needsMapViewUpdate: boolean,
    theme?: rootStyles,
    onChangeView?: Function,
    onSingleClick?: Function,
}

type StateType = {
}

const isEmpty = o => Object.keys(o || {}).length === 0

class Map extends React.Component<PropType, StateType> {
    static defaultProps: PropType = {
        baseLayer: 'osm',
        click: undefined,
        extent: undefined,
        layerAnimationData: undefined,
        layers: [],
        mapScale: 0,
        theme: {},

        // Map events default to noop
        onChangeView: () => {},
        onSingleClick: () => {},
    }

    map: OlMap
    mapContainer: any
    // The ResizeObserver
    ro: any
    constructor(props: PropType) {
        super(props)
        this.state = {
            hasHistoryPrev: false,
            hasHistoryNext: false,
            interaction: null,
        }
    }

    componentDidMount() {
        this.map = new OlMap('map')
        this.addResizeEvent()
        this.addMapEvents()
        this.updateMapFromProps({}, this.props)

        this.map.addNavigationHistoryChangedEvent(() => {
            this.setState({ ...this.state, hasHistoryPrev: this.map.hasHistoryPrev(), hasHistoryNext: this.map.hasHistoryNext() })
        })
    }

    componentWillReceiveProps(nextProps: PropType) {
        this.updateMapFromProps(this.props, nextProps)
    }

    componentWillUnmount() {
        this.ro.unobserve(this.mapContainer)
    }

    shouldComponentUpdate() {
        return false
    }

    addResizeEvent() {
        const handleResize = () => {
            const dim = this.mapContainer.getBoundingClientRect()
            this.resizeMap(dim)
        }

        // https://github.com/que-etc/resize-observer-polyfill
        this.ro = new ResizeObserver(() => {
            handleResize()
        })
        this.ro.observe(this.mapContainer)
        handleResize()
    }

    resizeMap(newDim: { width: string, height: string }) {
        const map = document.getElementById('map')
        if (map) {
            map.style.height = newDim.height
            this.map.updateSize()
        }
    }

    addMapEvents() {
        this.map.addViewChangedEvent((e: { view: ViewType }) => (this.props.onChangeView ? this.props.onChangeView(e.view) : {}))
        this.map.addSingleClickEvent((e: { lonlat: [number, number] }) => (this.props.onSingleClick ? this.props.onSingleClick(e, this) : {}))
    }

    simulateClick(click: ClickType) {
        const pixels = this.map.olmap.getPixelFromCoordinate(click.coordinate)
        // Not sure how this can be null
        if (pixels) {
            this.map.simulateEvent('singleclick', pixels[0], pixels[1],
                this.mapContainer.offsetWidth, this.mapContainer.offsetHeight)
        }
    }

    updateMapFromProps(oldprops: PropType | Object, newprops: PropType) {
        // If there are no previous props and the mapview is valid, then go ahead and update the map extent.
        const updateMap = Object.keys(oldprops).length === 0 && newprops.map.mapView.mapScale > 0
        const changed = prop => newprops[prop] &&
            JSON.stringify(oldprops[prop]) !== JSON.stringify(newprops[prop])

        if (oldprops.baseLayer !== newprops.baseLayer) this.map.setBaseLayer(newprops.baseLayer)
        if (changed('click') && newprops.click) this.simulateClick(newprops.click)
        if (changed('mapScale') && newprops.mapScale) this.map.setMapScale(newprops.mapScale)
        if (newprops.map.needsMapExtentUpdate) {
            this.map.setExtent(newprops.map.extent)
            newprops.mapExtentUpdateHandled()
        }
        if (newprops.map.needsMapViewUpdate || updateMap) {
            this.map.setView(newprops.map.mapView)
            newprops.mapViewUpdateHandled()
        }
        if (!isEmpty(newprops.layerAnimationData)) {
            this.map.animate(newprops.layerAnimationData)
        }
        if (isEmpty(newprops.layerAnimationData)) {
            this.map.animate()
        }
    }

    /**
     * A placemark is an overlay with extra content management.
     * @param args
     */
    addPlacemark(...args: any[]) {
        return this.map.addPlacemark(...args)
    }

    updatePlacemark(...args: any[]) {
        return this.map.updatePlacemark(...args)
    }

    movePlacemarkToTop(...args: any[]) {
        return this.map.movePlacemarkToTop(...args)
    }

    removePlacemark(...args: any[]) {
        return this.removeOverlay(...args)
    }

    removePlacemarks(...args: any[]) {
        return this.removeOverlays(...args)
    }

    /**
     * Create a map overlay directly.
     * @param content
     */
    addOverlay(content: string) {
        // Add to document
        const popupDiv = document.createElement('div')
        ReactDOM.render(<span>{content}</span>, popupDiv)
        this.map.addOverlay(popupDiv)
    }

    removeOverlay(...args: any[]) {
        this.map.removeOverlay(...args)
    }

    removeOverlays() {
        this.map.removeOverlays()
    }

    render() {
        return (
            <div
                id="map"
                className={this.props.theme.map}
                ref={(container) => {
                    this.mapContainer = container
                }}
            >
                <div
                    id="mouse-position"
                    style={{
                        bottom: 0,
                        color: 'orange',
                        padding: 10,
                        position: 'absolute',
                        right: '100px',
                        textShadow: '1px 1px white',
                        zIndex: 100,
                    }}
                />
            </div>
        )
    }
}

export default Map
